import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEOHead from "@/components/layout/SEOHead";
import { useContent } from "@/hooks/useContent";
import { ArrowUpRight, Calendar, Heart, Users, Sparkles, MapPin, Clock, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Convert a rich-text CMS value into clean plain text.
 * Handles the common cases the "Page Copy" editor emits:
 *  - wraps content in <p>...</p>
 *  - inserts &nbsp;, &middot;, &amp; entities
 *  - occasional <br>, <span>, etc.
 * We use the browser's DOM parser when available so entities decode correctly,
 * with a regex fallback for SSR / test environments.
 */
function stripHtml(input) {
  if (!input) return "";
  // Insert a space before every closing block-level tag so adjacent blocks
  // don't concatenate their text (e.g. "<p>Friday</p><p>6:30 PM</p>" -> "Friday 6:30 PM").
  const withGaps = String(input).replace(/<\/(p|div|br|li|h[1-6]|tr|td)>/gi, " </$1>");
  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(withGaps, "text/html");
      return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
    } catch (_) {
      /* fall through */
    }
  }
  return withGaps
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&middot;/gi, "·")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

// Real TIOB photos
const IMG_COLLAGE = "/wp-content/uploads/2025/08/collage8.4-791x1024.jpeg";
const IMG_WELCOME = "/wp-content/uploads/2023/09/WEL-2-scaled.jpg";
const IMG_MEMBER = "/wp-content/uploads/2023/09/botom-pic2.jpg";
const IMG_FOLLOW = "/wp-content/uploads/2023/09/botom-pic3.jpg";
const IMG_TORAH = "/wp-content/uploads/2025/12/Rabbi-Holding-Torah.jpg";

export default function Home() {
  // Editable via Admin → Content Blocks. Keys:
  //   home.shabbat.time  →  "Friday · 7:00 PM"
  //   home.torah.time    →  "Saturday · 9:30 AM"
  // The CMS rich-text editor saves values wrapped in HTML (e.g. <p>...</p>),
  // so we strip tags/entities before rendering into a plain-text slot.
  const { body: rawShabbat } = useContent("home.shabbat.time", "Friday · 7:00 PM");
  const { body: rawTorah } = useContent("home.torah.time", "Saturday · 9:30 AM");
  const { body: holidayBody, loading: holidayLoading } = useContent("home.holidays_block", "");
  const shabbatTime = stripHtml(rawShabbat) || "Friday · 7:00 PM";
  const torahTime = stripHtml(rawTorah) || "Saturday · 9:30 AM";
  const showHolidayBlock = !holidayLoading && !!(holidayBody && holidayBody.trim() && holidayBody !== "<p></p>");
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/events/upcoming?limit=8`).then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, []);

  return (
    <>
      <SEOHead
        title="Reform Synagogue in Brevard County, FL"
        description="Temple Israel of Brevard - the Reform synagogue serving Melbourne, Viera, Merritt Island, and Brevard County, Florida since 1965. An all-inclusive, LGBTQ+ welcoming place of worship. Shabbat services, religious school, and a warm community awaits."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              name: "Temple Israel of Brevard",
              url: "https://www.templeisraelofbrevard.org/",
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "What is a synagogue?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "A synagogue is a Jewish house of worship, learning, and community. At Temple Israel of Brevard, our synagogue in Brevard County, Florida hosts weekly Shabbat services, religious school, adult education, and lifecycle ceremonies. Anyone is welcome to visit - you do not need to be Jewish to attend a service.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Where is your synagogue located in Brevard County, Florida?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Temple Israel of Brevard is located at 7300 Lake Andrew Drive, Melbourne, FL 32940, serving all of Brevard County, Florida - Melbourne, Viera, Merritt Island, Cocoa Beach, and the wider Space Coast.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Do I need to be a member of the synagogue to attend a service?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "No. Shabbat services and most events at our synagogue are open to everyone - members, visitors, and the curious. Just walk in on any Friday night at 7:00 PM; you'll find a warm welcome.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What kind of synagogue is Temple Israel of Brevard?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Temple Israel of Brevard is a Reform synagogue and proud member of the Union for Reform Judaism. We are all-inclusive and LGBTQ+ welcoming, with warm homes for interfaith families, Jews of color, singles, seniors, and snowbirds.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Is there a Hebrew school or religious school at your synagogue?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes. Our Religious School and Hebrew school meet Sunday mornings for children in grades K-7, and is included free with family membership. We also offer adult Hebrew, weekly Torah Study, and a Path to Judaism program for those becoming Jewish.",
                  },
                },
              ],
            },
          ],
        }}
      />

      {/* HERO - bright, warm, welcoming */}
      <section className="relative overflow-hidden hero-glow" data-testid="home-hero">
        {/* gentle decorative blobs */}
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[28rem] h-[28rem] bg-[var(--blue)]/10 blob" />
        <div aria-hidden="true" className="absolute -bottom-32 -left-24 w-[24rem] h-[24rem] bg-[var(--honey)]/20 blob" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="eyebrow mb-6">★ Reform Synagogue · Brevard County, FL · Est. 1965</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.25rem] leading-[0.98] text-[var(--blue-ink)]">
              Welcome home to{" "}
              <span className="text-[var(--blue)]">our Jewish&nbsp;family.</span>
            </h1>
            <p className="mt-7 text-lg lg:text-xl text-[var(--ink-soft)] max-w-xl leading-relaxed">
              For sixty years, Temple Israel of Brevard has been a warm, vibrant Reform congregation
              where music fills the sanctuary, friendships are forged for a lifetime, and{" "}
              <em>every</em> seeker is welcomed - interfaith families, LGBTQ+ individuals, Jews of
              color, singles, snowbirds, and everyone in between.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/about" className="btn-primary" data-testid="hero-about-cta">
                Discover TIOB <ArrowUpRight size={18} />
              </Link>
              <Link to="/worship/shabbat" className="btn-ghost" data-testid="hero-worship-cta">
                Join us for Shabbat
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[var(--ink-soft)]">
              <span className="inline-flex items-center gap-2"><Star size={14} className="text-[var(--honey)] fill-[var(--honey)]" /> Member, Union for Reform Judaism</span>
              <span className="hidden sm:inline text-[var(--muted)]">·</span>
              <span className="inline-flex items-center gap-2"><Heart size={14} className="text-[var(--blue)]" /> Inclusive · Music-filled · Joyful</span>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-2 relative">
            <div className="relative">
              <div aria-hidden="true" className="absolute -inset-3 bg-[var(--blue)]/10 rounded-[2rem] rotate-[-3deg]" />
              <img
                src={IMG_COLLAGE}
                alt="Welcoming community gathering"
                className="relative rounded-[2rem] shadow-2xl w-full object-cover h-[26rem] lg:h-[34rem]"
                loading="eager"
              />
              {/* This week card - overlapping */}
              <div className="absolute -bottom-8 -left-4 md:-left-10 bg-white rounded-2xl shadow-xl border border-[var(--line)] p-5 w-[18rem]">
                <p className="eyebrow mb-3">★ This Week</p>
                <div className="space-y-3">
                  <div>
                    <div className="font-display text-lg text-[var(--blue-ink)]">Shabbat Service</div>
                    <div className="text-[var(--ink-soft)] text-xs flex items-center gap-2 mt-0.5" data-testid="home-shabbat-time"><Clock size={12} /> {shabbatTime}</div>
                  </div>
                  <div className="border-t border-[var(--line)]" />
                  <div>
                    <div className="font-display text-lg text-[var(--blue-ink)]">Torah Study</div>
                    <div className="text-[var(--ink-soft)] text-xs flex items-center gap-2 mt-0.5" data-testid="home-torah-time"><Clock size={12} /> {torahTime}</div>
                  </div>
                  <Link to="/events" className="block text-[var(--blue-deep)] text-sm font-medium hover:text-[var(--blue)] pt-1" data-testid="hero-full-calendar">
                    Full calendar →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HEBREW WELCOME - pull quote */}
      <section className="bg-white pt-24 lg:pt-28 pb-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <p className="eyebrow mb-5">ברוכים הבאים · Bruchim Habaim</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[var(--blue-ink)] leading-[1.15]">
            <em>“Find yourself a rabbi, acquire for yourself a friend, and judge every person on the positive side.”</em>
          </h2>
          <p className="mt-5 text-sm font-mono-accent uppercase tracking-widest text-[var(--muted)]">- Pirke Avot 1:6</p>
        </div>
      </section>

      {/* ABOUT INTRO */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="eyebrow mb-4">★ About TIOB</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              A welcoming Reform Congregation - where everyone has a seat at the table.
            </h2>
            <p className="mt-6 text-[var(--ink-soft)] leading-relaxed text-lg">
              Founded on Merritt Island in 1965 and now home in the Viera region of Melbourne,
              TIOB is a proud member of the Union for Reform Judaism. We are music-filled,
              actively involved, and deeply rooted in friendship.
            </p>
            <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">
              Our doors are open to interfaith couples and families, LGBTQ+ individuals,
              Jews of color, singles, couples, seniors, and snowbirds. If you're searching
              for Jewish community in Brevard County - you're already part of ours.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/about" className="btn-primary" data-testid="home-about-cta">Read our story</Link>
              <Link to="/rabbi" className="btn-ghost" data-testid="home-rabbi-cta">Meet Rabbi Fanwick</Link>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 grid grid-cols-2 gap-4">
            <div className="space-y-4 mt-10">
              <img src={IMG_TORAH} alt="Rabbi holding the Torah" className="rounded-2xl shadow-md w-full h-56 object-cover" loading="lazy" />
              <img src={IMG_MEMBER} alt="TIOB members" className="rounded-2xl shadow-md w-full h-44 object-cover" loading="lazy" />
            </div>
            <div className="space-y-4">
              <img src={IMG_FOLLOW} alt="Community gathering" className="rounded-2xl shadow-md w-full h-44 object-cover" loading="lazy" />
              <img src={IMG_WELCOME} alt="Welcome to TIOB" className="rounded-2xl shadow-md w-full h-56 object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* HOLIDAY SCHEDULE - admin-editable block (only rendered if the
          admin has added content). Ideal spot for the current-year holiday
          schedule, high-holy-days ticket info, or any timely announcement. */}
      {showHolidayBlock && (
        <section className="bg-[var(--cream)] py-16 lg:py-20 border-y border-[var(--line-warm)]" data-testid="home-holidays-block">
          <div className="max-w-4xl mx-auto px-5 lg:px-10">
            <div
              className="prose prose-lg max-w-none text-[var(--ink)]"
              dangerouslySetInnerHTML={{ __html: holidayBody }}
            />
          </div>
        </section>
      )}

      {/* PILLARS */}
      <section className="bg-[var(--cream)] py-20 lg:py-28 border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow mb-4">★ Our Pillars</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              Four cornerstones of life at Temple Israel
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Pillar icon={Sparkles} title="Worship" text="Music-filled Shabbat services and lifecycle moments led with warmth by Rabbi Fanwick." to="/worship" />
            <Pillar icon={Users} title="Community" text="Mahjong, Men's Club breakfasts, Torah Study, and friendships that last a lifetime." to="/membership" />
            <Pillar icon={Calendar} title="Education" text="Religious School for grades K-7 (free with membership), adult Hebrew, and Torah study every Saturday." to="/education" />
            <Pillar icon={Heart} title="Giving" text="Tikkun olam in action - your tzedakah sustains our sanctuary, school, and outreach." to="/donate" />
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS - removed; consolidated into the "This Week" section above */}

      {/* SYNAGOGUE LIFE - content pillar targeting the broad "synagogue" and
          "Brevard County, Florida" queries. Uses the terms naturally in H2s,
          H3s and body copy without disturbing our #1-ranked landing pages. */}
      <section className="bg-white py-20 lg:py-28" data-testid="synagogue-life-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <div className="max-w-3xl mb-12">
            <p className="eyebrow mb-4">★ Synagogue Life on the Space Coast</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              A welcoming synagogue at the heart of Brevard County, Florida.
            </h2>
            <p className="mt-6 text-[var(--ink-soft)] text-lg leading-relaxed">
              A synagogue is more than a house of worship &mdash; it&rsquo;s a
              place of learning, community, and lifecycle. For sixty years,
              Temple Israel of Brevard has been the Reform synagogue that
              Jewish families in <strong>Melbourne, Viera, Merritt Island,
              Cocoa Beach</strong> and the wider Space Coast have called home.
              Whether you grew up in a big-city synagogue and moved to
              Brevard County, Florida for the beaches, or you&rsquo;re curious
              about Jewish life for the first time, our doors are open.
            </p>
            <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">
              As Brevard County&rsquo;s Reform synagogue, we serve a wide
              region: from the barrier-island communities of Cocoa Beach and
              Satellite Beach to the growing neighborhoods of Viera and West
              Melbourne. We&rsquo;re a proud member of the Union for Reform
              Judaism and the only Reform-affiliated synagogue on Florida&rsquo;s
              Space Coast.
            </p>
          </div>

          {/* FAQ block - mirrors the FAQPage JSON-LD above so search engines
              can pull rich-result snippets straight from the visible page. */}
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
            <FaqItem q="What is a synagogue?">
              A synagogue is a Jewish house of worship, learning, and community.
              At our synagogue in Brevard County we hold weekly Shabbat services,
              religious school, adult education, and lifecycle ceremonies.
              Anyone is welcome to visit &mdash; you do not need to be Jewish to
              attend a service.
            </FaqItem>
            <FaqItem q="Where is your synagogue in Brevard County?">
              Temple Israel of Brevard is at 7300 Lake Andrew Drive, Melbourne,
              FL 32940, serving all of Brevard County, Florida &mdash; Melbourne,
              Viera, Merritt Island, Cocoa Beach, and the wider Space Coast.
            </FaqItem>
            <FaqItem q="Do I need to be a member to attend?">
              No. Shabbat services and most events at our synagogue are open to
              everyone. Just walk in on any Friday night at 7:00 PM &mdash;
              you&rsquo;ll find a warm welcome.
            </FaqItem>
            <FaqItem q="What kind of synagogue is TIOB?">
              A Reform synagogue and proud member of the Union for Reform
              Judaism. We are all-inclusive and LGBTQ+ welcoming, with warm
              homes for interfaith families, Jews of color, singles, seniors,
              and snowbirds across Brevard County.
            </FaqItem>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/about" className="btn-primary" data-testid="synagogue-life-about-cta">
              About our synagogue <ArrowUpRight size={16} />
            </Link>
            <Link to="/worship/shabbat" className="btn-ghost">
              Plan your first Shabbat visit
            </Link>
          </div>
        </div>
      </section>

      {/* WARM CTA strip */}
      <section className="bg-[var(--blue-ink)] py-20 lg:py-24 text-white relative overflow-hidden">
        <div aria-hidden="true" className="absolute -bottom-24 -right-24 w-96 h-96 bg-[var(--blue-bright)]/20 blob" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[var(--blue-bright)] mb-4">
            ★ Visit Us
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight">
            Come as you are. <span className="text-[var(--blue-bright)]">Stay as you wish.</span>
          </h2>
          <p className="mt-6 text-white/80 max-w-2xl mx-auto leading-relaxed text-lg">
            The best way to meet TIOB is to join us for a Friday-night Shabbat service.
            No reservation needed - just walk in and you'll find someone warm to sit with.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/worship/shabbat" className="btn-warm" data-testid="home-visit-cta">
              Plan a visit
            </Link>
            <Link to="/contact" className="btn-ghost !bg-transparent !text-white !border-white hover:!bg-white hover:!text-[var(--blue-ink)]">
              Email the rabbi
            </Link>
          </div>
        </div>
      </section>

      {/* DONATE STRIP - warm */}
      <section className="bg-[var(--cream)] py-20 lg:py-24 border-b border-[var(--line-warm)]">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <p className="eyebrow mb-4">★ Tzedakah · Repair the World</p>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
            Your generosity keeps our doors open and our community thriving.
          </h2>
          <p className="mt-5 text-[var(--ink-soft)] max-w-2xl mx-auto text-lg">
            Every gift - whether <em>chai</em> ($18) or beyond - strengthens TIOB.
          </p>
          <Link to="/donate" className="btn-primary mt-8" data-testid="home-donate-cta">
            <Heart size={16} /> Make a donation
          </Link>
        </div>
      </section>
    </>
  );
}

function Pillar({ icon: Icon, title, text, to }) {
  return (
    <Link to={to} className="card-soft group p-7 flex flex-col bg-white" data-testid={`pillar-${title.toLowerCase()}`}>
      <div className="w-14 h-14 rounded-2xl bg-[var(--blue)]/10 text-[var(--blue-deep)] flex items-center justify-center mb-5 group-hover:bg-[var(--blue)] group-hover:text-white transition-colors">
        <Icon size={24} />
      </div>
      <h3 className="font-display text-2xl text-[var(--blue-ink)]">{title}</h3>
      <p className="mt-2 text-[var(--ink-soft)] text-sm leading-relaxed flex-1">{text}</p>
      <span className="mt-5 text-[var(--blue-deep)] text-sm font-medium inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
        Learn more <ArrowUpRight size={14} />
      </span>
    </Link>
  );
}

function FaqItem({ q, children }) {
  return (
    <div>
      <h3 className="font-display text-xl text-[var(--blue-ink)] leading-snug">{q}</h3>
      <p className="mt-2 text-[var(--ink-soft)] leading-relaxed">{children}</p>
    </div>
  );
}

function ThisWeekSection({ events }) {
  if (!events || events.length === 0) {
    return (
      <section className="bg-white py-16 lg:py-20 border-t border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 text-center">
          <p className="eyebrow mb-3">★ This Week at TIOB</p>
          <p className="text-[var(--ink-soft)]">Check back soon for this week's events.</p>
        </div>
      </section>
    );
  }
  const featured = events.find((e) => e.featured) || events[0];
  const rest = events.filter((e) => e.id !== featured.id).slice(0, 5);

  return (
    <section className="bg-white py-20 lg:py-24" data-testid="home-this-week">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="eyebrow mb-3">★ This Week at TIOB</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              What's happening
            </h2>
          </div>
          <Link to="/calendar/weekly" className="btn-ghost" data-testid="home-weekly-cta">
            Weekly Calendar <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid lg:grid-cols-12 gap-5">
          {/* Featured event - large */}
          <article className="lg:col-span-7 card-soft overflow-hidden bg-white flex flex-col" data-testid={`featured-event-${featured.id}`}>
            {featured.flyer_url ? (
              <img src={featured.flyer_url} alt={featured.title} className="w-full h-72 object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-72 bg-gradient-to-br from-[var(--blue)]/15 to-[var(--honey)]/20 flex items-center justify-center">
                <DateBlock dateStr={featured.start_date} large />
              </div>
            )}
            <div className="p-7 flex-1 flex flex-col">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono-accent text-xs uppercase tracking-widest text-[var(--blue-deep)] bg-[var(--blue)]/8 px-2.5 py-1 rounded-full">
                  Featured
                </span>
                <span className="font-mono-accent text-xs uppercase tracking-widest text-[var(--muted)]">{featured.category}</span>
              </div>
              <h3 className="font-display text-3xl text-[var(--blue-ink)] mt-3">{featured.title}</h3>
              <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">{featured.description}</p>
              <div className="mt-4 text-sm text-[var(--muted)] flex flex-wrap gap-x-5 gap-y-1">
                <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {new Date(featured.start_date).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}{featured.start_time ? ` · ${featured.start_time}` : ""}</span>
                {featured.location && <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {featured.location}</span>}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                {featured.rsvp_url && (
                  <a href={featured.rsvp_url} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm" data-testid={`rsvp-${featured.id}`}>RSVP</a>
                )}
                {featured.payment_url && (
                  <a href={featured.payment_url} target="_blank" rel="noopener noreferrer" className="btn-warm text-sm">Pay / Register</a>
                )}
                {featured.details_url && (
                  <a href={featured.details_url} target="_blank" rel="noopener noreferrer" className="btn-ghost text-sm">Full details</a>
                )}
                <Link to="/calendar/weekly" className="btn-ghost text-sm">See the week</Link>
              </div>
            </div>
          </article>

          {/* Compact list */}
          <div className="lg:col-span-5 space-y-3">
            {rest.map((ev) => (
              <article key={ev.id} className="card-soft p-5 bg-white flex gap-4" data-testid={`mini-event-${ev.id}`}>
                <DateBlock dateStr={ev.start_date} />
                <div className="flex-1 min-w-0">
                  <p className="font-mono-accent text-xs uppercase tracking-widest text-[var(--blue-deep)]">{ev.category}</p>
                  <h4 className="font-display text-lg text-[var(--blue-ink)] mt-0.5 leading-tight">{ev.title}</h4>
                  <p className="text-xs text-[var(--muted)] mt-1 flex flex-wrap gap-x-3">
                    {ev.start_time && <span className="inline-flex items-center gap-1"><Clock size={11} /> {ev.start_time}</span>}
                    {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {ev.location}</span>}
                  </p>
                  {(ev.rsvp_url || ev.payment_url) && (
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      {ev.rsvp_url && <a href={ev.rsvp_url} target="_blank" rel="noopener noreferrer" className="text-[var(--blue-deep)] font-semibold hover:underline">RSVP →</a>}
                      {ev.payment_url && <a href={ev.payment_url} target="_blank" rel="noopener noreferrer" className="text-[var(--blue-deep)] font-semibold hover:underline">Register →</a>}
                    </div>
                  )}
                </div>
              </article>
            ))}
            {rest.length === 0 && (
              <p className="text-[var(--muted)] text-sm">No additional events this week.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function DateBlock({ dateStr, large }) {
  const d = new Date(dateStr);
  return (
    <div className={`shrink-0 text-center bg-[var(--blue)]/10 text-[var(--blue-ink)] rounded-xl ${large ? "px-6 py-5 min-w-[120px]" : "px-3 py-2 min-w-[60px]"}`}>
      <div className={`font-mono-accent uppercase tracking-widest text-[var(--blue-deep)] ${large ? "text-xs" : "text-xs"}`}>
        {d.toLocaleDateString("en-US", { month: "short" })}
      </div>
      <div className={`font-display leading-none mt-1 ${large ? "text-6xl" : "text-2xl"}`}>{d.getDate()}</div>
    </div>
  );
}
